import React, { useMemo } from 'react';
import { Mail, Phone } from 'lucide-react';
import { Button } from '@mts/ui';
import { usePublicTenant } from '../hooks/usePublicTenant';
import { formatPhoneNumber } from '../utils/phone';

function cleanString(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function getSettingValue(tenant, key) {
  const list = Array.isArray(tenant?.tenantSettings) ? tenant.tenantSettings : [];
  return list.find((item) => item?.settingKey === key)?.settingValue;
}

function resolveContactDetails({ tenant, spotlightName, contactEmail, contactPhone }) {
  const contact = tenant?.websiteContact || {};
  const siteName =
    cleanString(tenant?.publicName) ||
    cleanString(contact.publicDisplayName) ||
    cleanString(spotlightName) ||
    cleanString(getSettingValue(tenant, 'tenant.name')) ||
    cleanString(tenant?.name) ||
    'Local Spotlight';

  return {
    siteName,
    email: cleanString(contact.contactEmail) || cleanString(contactEmail),
    phone: cleanString(contact.contactPhone) || cleanString(contactPhone),
  };
}

function ContactAction({ icon: Icon, label, value, href, buttonLabel }) {
  if (!value) return null;

  return (
    <article className="rounded-lg border border-border bg-background p-5 shadow-sm">
      <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <h2 className="mt-4 text-lg font-semibold text-text-primary">{label}</h2>
      <p className="mt-2 break-words text-base leading-7 text-text-secondary">{value}</p>
      <Button asChild variant="primary" size="lg" className="mt-5 gap-2 rounded-md text-sm">
        <a href={href}>
          {buttonLabel}
        </a>
      </Button>
    </article>
  );
}

function ContactPage() {
  const { tenant, spotlightName, contactEmail, contactPhone } = usePublicTenant();
  const details = useMemo(
    () => resolveContactDetails({ tenant, spotlightName, contactEmail, contactPhone }),
    [tenant, spotlightName, contactEmail, contactPhone],
  );

  const hasContact = Boolean(details.email || details.phone);

  return (
    <div className="min-h-screen overflow-x-hidden bg-surface text-text-primary">
      <section className="relative left-1/2 w-screen -translate-x-1/2 bg-surface">
        <div className="mx-auto max-w-6xl px-5 py-14 md:px-8 lg:py-16">
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">Contact Us</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-bold leading-tight text-text-primary sm:text-5xl">
            Contact {details.siteName}
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-8 text-text-secondary">
            Reach the local team by email or phone.
          </p>

          {hasContact ? (
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <ContactAction
                icon={Mail}
                label="Email"
                value={details.email}
                href={`mailto:${details.email}`}
                buttonLabel="Send Email"
              />
              <ContactAction
                icon={Phone}
                label="Phone"
                value={formatPhoneNumber(details.phone)}
                href={`tel:${details.phone.replace(/[^+\d]/g, '')}`}
                buttonLabel="Call"
              />
            </div>
          ) : (
            <div className="mt-8 rounded-lg border border-border bg-background p-5 text-text-secondary">
              Contact details are not available yet.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default ContactPage;
